import { gql } from "@apollo/client";
import { PageContentStack } from "atoms/Stack";
import Stack from "atoms/Stack";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { ONSEN_ENV, ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX } from "helpers/environment";
import { gaSetSharedEventParams } from "helpers/gtag";
import { SPACING } from "helpers/spacings";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import { PlayerListDropdown } from "molecules/PlayerList";
import React, { useEffect, useMemo, useState } from "react";
import NotFoundPage from "shared/NotFoundPage";
import PageTitle from "shared/PageTitle";
import UnauthorisedPage from "shared/UnauthorisedPage";

import BoxScore, { BoxScoreFragment } from "./games/BoxScore";
import GameOverviewHighlights from "./games/GameOverviewHighlights";
import GameOverviewPageHeader, { GameOverviewPageHeaderFragment } from "./games/GameOverviewPageHeader";
import GameOverviewReels, {
  GameOverviewReelsFragment,
  GameOverviewReelsGamePlayerFragment,
} from "./games/GameOverviewReels";
import UpdateStubbedUserEmailModal from "./games/GameOverviewUpdateStubbedUserEmailModal";
import PointChart from "./games/PointChart";
import ShotChart from "./games/ShotChart";

const GameOverviewPageGamePlayerFragment = gql`
  fragment GameOverviewPageGamePlayerFragment on GamePlayer {
    id
    playerNumber
    ...GameOverviewReelsGamePlayerFragment
  }
  ${GameOverviewReelsGamePlayerFragment}
`;

export default function GameOverviewPage({ gameId }) {
  useEnforceAuth({ enforceAuthenticated: true });
  const [gamePlayerId, gamePlayerIdSet] = useRouteState("gamePlayerId");
  const [tab] = useRouteState("tab");
  const [stubbedModalOpen, setStubbedModalOpen] = useState(false);
  const isPublicLeaguePage = useMemo(() => {
    if (typeof window === "undefined" || !ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX) {
      return false;
    }

    let locationHost = window.location.host;
    if (ONSEN_ENV === "test") {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const stubHost = urlSearchParams.get("_stub_location_host");
      if (stubHost) {
        locationHost = stubHost;
      }
    }

    return locationHost.endsWith(ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX);
  }, []);

  const [currentUserData, { loading: loadingUser }] = useData(gql`
    query GameOverviewPage_current_user {
      currentUser {
        id
        email
        stubbed
      }
    }
  `);

  const [permissionData, { loading: loadingPermission }] = useData(
    gql`
      query GameOverviewPage_permission($gameId: ID!) {
        game(id: $gameId) {
          id
          canOnsenAccessVideo
        }
      }
    `,
    { gameId },
  );

  const [data, { loading: loadingGame }] = useData(
    gql`
      query GameOverviewPage($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
          }
          org {
            gaId
          }
          statsVisible
          gameVideoStatus
          ...GameOverviewPageHeaderFragment
          ...GameOverviewReelsFragment
          ...BoxScoreFragment
        }
      }
      ${GameOverviewPageHeaderFragment}
      ${GameOverviewReelsFragment}
      ${BoxScoreFragment}
    `,
    { gameId },
    { skip: permissionData && !permissionData.game?.canOnsenAccessVideo },
  );

  const [dataDefaultGamePlayer, { loading: loadingDefaultPlayer }] = useData(
    gql`
      query GameOverviewPage_defaultGamePlayer($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
            ...GameOverviewPageGamePlayerFragment
          }
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gameId },
    { skip: !!gamePlayerId },
  );

  const [dataGamePlayer, { loading: loadingPlayer }] = useData(
    gql`
      query GameOverviewPage_gamePlayer($gamePlayerId: ID!) {
        gamePlayer(id: $gamePlayerId) {
          id
          ...GameOverviewPageGamePlayerFragment
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gamePlayerId },
    { skip: !gamePlayerId },
  );

  useEffect(() => {
    if (isPublicLeaguePage) {
      return;
    }
    if (currentUserData?.currentUser?.stubbed === true) {
      setStubbedModalOpen(true);
    }
  }, [currentUserData?.currentUser?.stubbed, isPublicLeaguePage]);

  const game = data?.game;

  useEffect(() => {
    const gaId = game?.org?.gaId;
    if (!gaId) {
      return;
    }
    gaSetSharedEventParams({ org_id: gaId });
  }, [game?.org?.gaId]);

  const gamePlayer = dataGamePlayer?.gamePlayer || dataDefaultGamePlayer?.game?.currentUserDefaultGamePlayer;
  const playerListButtonLabel =
    gamePlayer && gamePlayer.person
      ? `${gamePlayer.person.fullNameWithAnonymisation}${gamePlayer.playerNumber ? ` #${gamePlayer.playerNumber}` : ""}`
      : "Select player";
  const handlePlayerSelect = (player) => {
    if (player?.id) {
      gamePlayerIdSet?.(player.id);
    }
  };
  const isLoading = loadingUser || loadingPermission || loadingGame || loadingPlayer || loadingDefaultPlayer;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (permissionData?.game && !permissionData.game.canOnsenAccessVideo) {
    return <UnauthorisedPage currentUser={currentUserData?.currentUser} />;
  }

  if (!game) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageTitle title="Game Overview" />
      {!isPublicLeaguePage && (
        <UpdateStubbedUserEmailModal open={stubbedModalOpen} onClose={() => setStubbedModalOpen(false)} />
      )}

      <GameOverviewPageHeader game={game} tab={tab} />
      {game?.statsVisible && (
        <PageContentStack>
          <Stack style={{ width: `calc(100vw - ${SPACING} * 2)`, margin: "0 auto", maxWidth: MAX_CONTENT_WIDTH }}>
            {gamePlayer && (
              <>
                <PlayerListDropdown
                  game={game}
                  selectedPlayerId={gamePlayer?.id}
                  onSelect={handlePlayerSelect}
                  buttonLabel={playerListButtonLabel}
                  isLoading={!Array.isArray(game?.gamePlayers)}
                />
                {game?.gameVideoStatus === "available" && (!tab || tab === "reels") && (
                  <GameOverviewReels gamePlayer={gamePlayer} />
                )}
                {game?.gameVideoStatus === "available" && (!tab || tab === "highlights") && (
                  <GameOverviewHighlights gameId={gameId} gamePlayer={gamePlayer} />
                )}
                {(!tab || tab === "shotchart") && (
                  <ShotChart gameId={gameId} currentUserGamePlayerId={gamePlayer?.id} />
                )}
              </>
            )}

            {(!tab || tab === "boxscore") && <BoxScore game={game} gamePlayer={gamePlayer} />}
            {(!tab || tab === "pointchart") && <PointChart gameId={gameId} />}
          </Stack>
        </PageContentStack>
      )}
    </>
  );
}
